import {
  Collaboration,
  MutationMarkApplicantAsFavoriteArgs,
  MutationRemoveApplicantAsFavoriteArgs,
} from '../../../../../gql/types'
import { Avatar } from '../../../../../elements'
import {
  Button,
  ButtonStyle,
  CollaborationStatus,
  Icon,
  IconType,
  NotificationType,
  StarRating,
} from '../../../../../components'
import { GridButtons } from '../GridButtons'
import { Link } from 'react-router-dom'
import { BrandRoutes } from '../../../../BrandRoutes'
import { useNotification } from '../../../../../utils/hooks'
import { useMutation } from '@apollo/client'
import {
  MARK_APPLICANT_AS_FAVORITE,
  REMOVE_APPLICANT_AS_FAVORITE,
} from '../../../../../gql'

interface IProps {
  collaboration: Collaboration
  setSelectedCollaboration(collaboration: Collaboration): void
  setIsCreatorSliderOpen(isSliderOpen: boolean): void
  className?: string
}

const GridItem = ({
  collaboration,
  setSelectedCollaboration,
  setIsCreatorSliderOpen,
  className,
}: IProps) => {
  const { addNotification } = useNotification()

  const [markApplicantAsFavorite, { loading: markApplicantAsFavoriteLoading }] =
    useMutation<Collaboration, MutationMarkApplicantAsFavoriteArgs>(
      MARK_APPLICANT_AS_FAVORITE,
    )
  const [
    removeApplicantAsFavorite,
    { loading: removeApplicantAsFavoriteLoading },
  ] = useMutation<Collaboration, MutationRemoveApplicantAsFavoriteArgs>(
    REMOVE_APPLICANT_AS_FAVORITE,
  )

  const onMarkApplicantAsFavorite = async () => {
    try {
      await markApplicantAsFavorite({
        variables: {
          collaborationId: collaboration.id,
        },
      })

      addNotification(
        'Applicant has been marked as a favorite.',
        NotificationType.success,
      )
    } catch (error) {
      console.log(error)
      addNotification(
        'There has been an error marking this applicant as favorite',
        NotificationType.error,
      )
    }
  }

  const onRemoveApplicantAsFavorite = async () => {
    try {
      await removeApplicantAsFavorite({
        variables: {
          collaborationId: collaboration.id,
        },
      })

      addNotification(
        'Applicant has been removed from favorites.',
        NotificationType.success,
      )
    } catch (error) {
      console.log(error)
      addNotification(
        'There has been an error removing this applicant as favorite',
        NotificationType.error,
      )
    }
  }

  return (
    <div
      key={collaboration.id}
      className='col-span-1 divide-y divide-gray-200 rounded-lg bg-white border border-gray-200 shadow mx-2'
    >
      <div className='cursor-pointer flex w-full items-center justify-between align-top space-x-4 p-4'>
        <Link
          to={BrandRoutes.collaborationViewProfile
            .replace(':id', `${collaboration.campaign.brand.id}`)
            .replace(':uuid', collaboration.uuid)}
          className='cursor-pointer flex gap-2 font-medium text-gray-900 hover:text-gray-800'
        >
          <div className='flex items-center'>
            <Avatar
              type='user'
              src={collaboration.creator.avatar?.url}
              size='sm'
              alt={collaboration.creator.firstName || ''}
            />
            <div className='ml-2'>
              <div className='flex text-gray-900 max-w-[150px] xs:max-w-[250px] sm:max-w-[400px] overflow-hidden truncate'>
                {collaboration.creator.firstName}
              </div>
              <div className='truncate mt-1 mb-0 flex flex-shrink-0 text-sm text-blue-600 hover:text-blue-500'>
                view profile
              </div>
            </div>
          </div>
        </Link>
        {collaboration.isFavorite && (
          <Button
            type='button'
            styleType={ButtonStyle.ghost}
            onClick={onRemoveApplicantAsFavorite}
            loading={removeApplicantAsFavoriteLoading}
            disabled={removeApplicantAsFavoriteLoading}
            icon={
              <Icon
                type={IconType.heartFilled}
                className='h-4 w-4 text-red-500'
                aria-hidden='true'
              />
            }
            iconPosition='left'
            tooltip='Remove applicant from shortlist'
          />
        )}
        {!collaboration.isFavorite && (
          <Button
            type='button'
            styleType={ButtonStyle.ghost}
            onClick={onMarkApplicantAsFavorite}
            loading={markApplicantAsFavoriteLoading}
            disabled={markApplicantAsFavoriteLoading}
            icon={
              <Icon
                type={IconType.heartFilled}
                className='h-4 w-4 text-gray-400'
                aria-hidden='true'
              />
            }
            iconPosition='left'
            tooltip='Shortlist applicant'
          />
        )}
      </div>
      <dl className='space-y-3 p-4'>
        <div className='grid grid-cols-2 items-center'>
          <dt className='text-sm font-medium text-gray-500'>Status</dt>
          <dd className='-ml-2 text-sm text-gray-900'>
            <CollaborationStatus status={collaboration.status} />
          </dd>
        </div>
        <div className='grid grid-cols-2 items-center'>
          <dt className='text-sm font-medium text-gray-500'>Reviews</dt>
          <dd className='text-sm text-gray-900'>
            <Link
              to={BrandRoutes.collaborationViewReviews
                .replace(':id', `${collaboration.campaign.brand.id}`)
                .replace(':uuid', collaboration.uuid)}
              className='cursor-pointer flex gap-2'
            >
              <StarRating
                ratingScore={collaboration?.creator?.metadata?.ratingsScore}
                ratingCount={collaboration?.creator?.metadata?.ratingsCount}
              />
            </Link>
          </dd>
        </div>
      </dl>
      <GridButtons
        collaboration={collaboration}
        key={`gridItem-${collaboration?.uuid}`}
        setSelectedCollaboration={setSelectedCollaboration}
        setIsCreatorSliderOpen={setIsCreatorSliderOpen}
      />
    </div>
  )
}

export { GridItem }
